import { Middleware } from "@microsoft/microsoft-graph-client";
import { Context } from "@microsoft/microsoft-graph-client/lib/src/IContext";
import { appendRequestHeader, setRequestHeader } from "@microsoft/microsoft-graph-client/lib/src/middleware/MiddlewareUtil";
import { IAuthenticationProvider, IdentityType } from "../interfaces/IAuthenticationProvider";
import { GetPrimaryIdentityInfo } from "../office-js/auth/IdentityUtil";
import { OnedriveBaseUrlFromAuthEnvironment } from "../oneDrive/OnedriveTokenProvider";
import { IS_DEV_BUILD } from "../../AppConstants";

/**
 * Middleware for large file uploads via the Graph SDK.
 *
 * Responsibilities:
 * 1. Adds `Prefer: pacToken=N` to createUploadSession POST requests so that
 *    ODSP returns a non-tempauth upload URL.
 * 2. For upload PUT requests that go directly to ODSP (bypassing AuthenticationHandler
 *    which only covers Graph-API base URLs), fills in the Authorization header.
 *    Token resource mirrors the Service Worker audio proxy:
 *    - ODP (MSA): OnedriveBaseUrlFromAuthEnvironment (e.g. "onedrive.readwrite")
 *    - ODB (enterprise): upload URL origin (e.g. "https://tenant.sharepoint.com")
 *      — requires a write-capable SPO preAuth permission to succeed.
 */
export class SpoUploadHandler implements Middleware {
    private static readonly AUTHORIZATION_HEADER = "Authorization";
    private static readonly PREFER_HEADER = "Prefer";
    private static readonly PREFER_PAC_TOKEN_VALUE = "pacToken=N";
    private static readonly CREATE_UPLOAD_SESSION_PATH = "/createUploadSession";
    private authProvider: IAuthenticationProvider;
    private nextMiddleware: Middleware | null = null;
    private identityType: IdentityType | null = null;

    public constructor(authProvider: IAuthenticationProvider) {
        this.authProvider = authProvider;
    }

    private async getIdentityType(): Promise<IdentityType> {
        if (this.identityType !== null) {
            return this.identityType;
        }
        if (IS_DEV_BUILD) {
            // GetPrimaryIdentityInfo is unavailable in local dev; default to ODB.
            this.identityType = IdentityType.ODB;
        } else {
            const identityInfo = await GetPrimaryIdentityInfo();
            this.identityType = identityInfo.type;
        }
        return this.identityType;
    }

    public async execute(context: Context): Promise<void> {
        try {
            const identityType = await this.getIdentityType();
            if (identityType === IdentityType.Unsupported) {
                return await this.nextMiddleware?.execute(context);
            }

            const requestUrl = typeof context.request === "string"
                ? context.request
                : (context.request as Request)?.url ?? "";

            // Add Prefer: pacToken=N so ODSP omits tempauth from the returned upload URL.
            if (requestUrl.includes(SpoUploadHandler.CREATE_UPLOAD_SESSION_PATH)) {
                appendRequestHeader(
                    context.request,
                    context.options,
                    SpoUploadHandler.PREFER_HEADER,
                    SpoUploadHandler.PREFER_PAC_TOKEN_VALUE
                );
            }

            // Fill in Authorization for upload PUT requests that bypass AuthenticationHandler.
            // AuthenticationHandler only sets auth for Graph URLs and deletes the header for
            // non-Graph URLs, so this condition is true only for non-Graph (ODSP) requests.
            const headers = context.options?.headers as Record<string, string> | undefined;
            if (!headers?.[SpoUploadHandler.AUTHORIZATION_HEADER]) {
                const tokenResource = identityType === IdentityType.ODP
                    ? OnedriveBaseUrlFromAuthEnvironment(this.authProvider.GetAuthEnvironment(), identityType)
                    : new URL(requestUrl).origin;
                const token = await this.authProvider.GetAccessToken({ resource: tokenResource });
                setRequestHeader(
                    context.request,
                    context.options,
                    SpoUploadHandler.AUTHORIZATION_HEADER,
                    `Bearer ${token}`
                );
            }
        } catch (e) {
            console.warn("[SpoUploadHandler] Failed to modify request headers:", e);
        }

        return await this.nextMiddleware?.execute(context);
    }

    public setNext(middleware: Middleware): void {
        this.nextMiddleware = middleware;
    }
}
